import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as RBCalendar, dayjsLocalizer, Views } from "react-big-calendar";
import dayjs from "dayjs";
import { Select, Drawer, DatePicker } from "antd";
import { RefreshCw, Calendar as CalendarIcon, Phone, User as UserIcon, ArrowRight, FileText, Clock } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { DEMO_CALENDAR_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import "react-big-calendar/lib/css/react-big-calendar.css";

const { RangePicker } = DatePicker;

const localizer = dayjsLocalizer(dayjs);

// soft palette — matches the PRIORITY_COLORS / STATUS_COLORS style used in TasksList
const STATUS_META = {
    scheduled: {
        label: "Scheduled",
        chip:  "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        bar:   "#06b6d4",
    },
    completed: {
        label: "Done",
        chip:  "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        bar:   "#10b981",
    },
    no_show: {
        label: "No Show",
        chip:  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        bar:   "#ef4444",
    },
    past_due: {
        label: "Past Due",
        chip:  "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        bar:   "#f59e0b",
    },
};

const FILTERS = ["All", "Scheduled", "Done", "No Show", "Past Due"];
const FILTER_TO_KEY = {
    "All": "all",
    "Scheduled": "scheduled",
    "Done": "completed",
    "No Show": "no_show",
    "Past Due": "past_due",
};

const DEFAULT_DURATION_MIN = 30;

// "All Time" range — wide enough to cover any historical or future-scheduled demo.
// The start is a fixed date so it is safe to hold at module scope; the end is
// derived from today and so has to be built on demand.
const ALL_TIME_START = dayjs("2020-01-01");
const allTimeEnd = () => dayjs().add(2, "year").endOf("year");
const allTimeRange = () => [ALL_TIME_START, allTimeEnd()];

// Quick presets for the date-range picker. Built per render rather than once at
// module load: every label here is relative to "now", and a tab left open past
// midnight would otherwise keep offering yesterday as "Today".
const datePresets = () => [
    { label: "All Time",     value: allTimeRange() },
    { label: "Today",        value: [dayjs().startOf("day"),                  dayjs().endOf("day")] },
    { label: "Tomorrow",     value: [dayjs().add(1, "day").startOf("day"),    dayjs().add(1, "day").endOf("day")] },
    { label: "This Week",    value: [dayjs().startOf("week"),                 dayjs().endOf("week")] },
    { label: "Next 7 Days",  value: [dayjs().startOf("day"),                  dayjs().add(7, "day").endOf("day")] },
    { label: "This Month",   value: [dayjs().startOf("month"),                dayjs().endOf("month")] },
    { label: "Next 30 Days", value: [dayjs().startOf("day"),                  dayjs().add(30, "day").endOf("day")] },
    { label: "Last 7 Days",  value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")] },
    { label: "Last 30 Days", value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")] },
];

const DemoCalendar = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [allEvents, setAllEvents] = useState([]);
    const [counts, setCounts] = useState({ scheduled: 0, completed: 0, no_show: 0, past_due: 0 });
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [assigneeId, setAssigneeId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("All");
    const [view, setView] = useState(Views.MONTH);
    const [date, setDate] = useState(new Date());
    const [dateRange, setDateRange] = useState(null);   // [dayjs, dayjs] | null
    const [selected, setSelected] = useState(null);

    // Effective fetch range — explicit date filter wins over the calendar's view-based range.
    const range = useMemo(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            return {
                from: dateRange[0].format("YYYY-MM-DD"),
                to:   dateRange[1].format("YYYY-MM-DD"),
            };
        }
        const cur = dayjs(date);
        let from, to;
        if (view === Views.MONTH) {
            from = cur.startOf("month").startOf("week");
            to   = cur.endOf("month").endOf("week");
        } else if (view === Views.WEEK) {
            from = cur.startOf("week");
            to   = cur.endOf("week");
        } else if (view === Views.DAY) {
            from = cur.startOf("day");
            to   = cur.endOf("day");
        } else {
            from = cur.startOf("day");
            to   = cur.add(30, "day").endOf("day");
        }
        return { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") };
    }, [date, view, dateRange]);

    // When the user picks a custom range, jump the calendar to it and switch to Agenda view
    // (best fit for arbitrary multi-week windows).
    const handleRangeChange = (values) => {
        setDateRange(values || null);
        if (values && values[0]) {
            setDate(values[0].toDate());
            setView(Views.AGENDA);
        }
    };

    // Agenda view defaults to 30 days from its current date; stretch it to cover the active range.
    const agendaLength = useMemo(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            return Math.max(1, dateRange[1].diff(dateRange[0], "day"));
        }
        return 30;
    }, [dateRange]);

    const isAllTime = !!(dateRange
        && dateRange[0] && dateRange[1]
        && dateRange[0].isSame(ALL_TIME_START, "day")
        && dateRange[1].isSame(allTimeEnd(), "day"));

    const loadEvents = useCallback(() => {
        setLoading(true);
        const filters = { from: range.from, to: range.to };
        if (assigneeId) filters.assignee_id = assigneeId;

        fetch(DEMO_CALENDAR_URL, filters)
            .then(res => {
                if (res?.status === "success") {
                    const rows = (res.data || []).map(d => {
                        const start = new Date(d.scheduled_at);
                        const end   = new Date(start.getTime() + DEFAULT_DURATION_MIN * 60 * 1000);
                        return { ...d, start, end, title: d.org_name || "Demo" };
                    });
                    setAllEvents(rows);
                    setCounts(res.counts || { scheduled: 0, completed: 0, no_show: 0, past_due: 0 });
                } else {
                    ToastMsg("error", res?.message || "Failed to load demos");
                }
            })
            .finally(() => setLoading(false));
    }, [range.from, range.to, assigneeId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Status filter is applied client-side so switching pills is instant and
    // never refetches — that also keeps the count totals stable.
    const events = useMemo(() => {
        const key = FILTER_TO_KEY[statusFilter];
        if (!key || key === "all") return allEvents;
        return allEvents.filter(e => e.derived_status === key);
    }, [allEvents, statusFilter]);

    useEffect(() => { loadEvents(); }, [loadEvents]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const eventStyleGetter = (event) => {
        const meta = STATUS_META[event.derived_status] || STATUS_META.scheduled;
        return {
            style: {
                backgroundColor: meta.bar,
                borderColor: meta.bar,
                color: "white",
                borderRadius: 4,
                border: "none",
                padding: "1px 6px",
                fontSize: 11,
                fontWeight: 500,
            },
        };
    };

    const filterCount = (filterLabel) => {
        if (filterLabel === "All") return Object.values(counts).reduce((a, b) => a + b, 0);
        return counts[FILTER_TO_KEY[filterLabel]] || 0;
    };

    const goToCustomer = (id) => {
        if (!id) return;
        setSelected(null);
        navigate(`/customers/customerdetails?id=${id}`);
    };

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Demos</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Calendar view of all scheduled demos</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => handleRangeChange(allTimeRange())}
                        className={cn(
                            "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border",
                            isAllTime
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        )}
                        title="Show every demo across all time"
                    >
                        All Time
                    </button>
                    <RangePicker
                        value={isAllTime ? null : dateRange}
                        onChange={handleRangeChange}
                        presets={datePresets()}
                        format="DD MMM YYYY"
                        allowClear
                        placeholder={["From", "To"]}
                    />
                    <Select
                        allowClear
                        placeholder="All assignees"
                        value={assigneeId}
                        onChange={(v) => setAssigneeId(v || null)}
                        style={{ width: 180 }}
                        options={users.map(u => ({ value: u.id, label: u.login_fname || u.login_name }))}
                        showSearch
                        optionFilterProp="label"
                    />
                    <button onClick={loadEvents}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
                        <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Filter pills (match TasksList) */}
            <div className="flex items-center gap-1 mb-4 flex-wrap">
                {FILTERS.map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                        className={cn("px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                            statusFilter === s
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                        )}>
                        {s} ({filterCount(s)})
                    </button>
                ))}
            </div>

            {/* Calendar surface */}
            <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 min-h-0">
                <RBCalendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    view={view}
                    onView={setView}
                    date={date}
                    onNavigate={setDate}
                    views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                    length={agendaLength}
                    popup
                    style={{ height: "100%", minHeight: 480 }}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={(e) => setSelected(e)}
                    tooltipAccessor={(e) => `${e.org_name} — ${STATUS_META[e.derived_status]?.label || e.derived_status}`}
                    components={{
                        event: ({ event }) => (
                            <div className="truncate">
                                <span className="font-medium">{dayjs(event.start).format("HH:mm")}</span>
                                <span className="mx-1">·</span>
                                <span>{event.org_name}</span>
                            </div>
                        ),
                    }}
                />
            </div>

            {/* Event detail drawer */}
            <Drawer
                open={!!selected}
                onClose={() => setSelected(null)}
                title={selected?.org_name || "Demo"}
                width={420}
                destroyOnClose
            >
                {selected && (
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full",
                                STATUS_META[selected.derived_status]?.chip)}>
                                {STATUS_META[selected.derived_status]?.label || selected.derived_status}
                            </span>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                {selected.current_stage}
                            </span>
                        </div>

                        <div className="text-sm space-y-3">
                            <div className="flex items-start gap-2.5">
                                <CalendarIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Scheduled</div>
                                    <div className="text-gray-900 dark:text-gray-100 font-medium">
                                        {dayjs(selected.scheduled_at).format("ddd, MMM D · HH:mm")}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-2.5">
                                <UserIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Assigned to</div>
                                    <div className="text-gray-900 dark:text-gray-100">{selected.assignee_name || "—"}</div>
                                </div>
                            </div>

                            {selected.phone && (
                                <div className="flex items-start gap-2.5">
                                    <Phone className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Contact</div>
                                        <div className="text-gray-900 dark:text-gray-100">{selected.phone}</div>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-start gap-2.5">
                                <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Logged by</div>
                                    <div className="text-gray-900 dark:text-gray-100">{selected.created_by_name || "—"}</div>
                                </div>
                            </div>

                            {selected.remarks && (
                                <div className="flex items-start gap-2.5 border-t border-gray-200 dark:border-gray-700 pt-3">
                                    <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Remarks</div>
                                        <div className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{selected.remarks}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button onClick={() => goToCustomer(selected.customer_id)}
                            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors mt-2">
                            Open Customer <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </Drawer>
        </ContainerCard>
    );
};

export default DemoCalendar;
